/**
 * Кодирует строку query-параметров
 * (!) Пустые параметры (которые кастятся к false) игнорируются
 * @param {Object} params параметры
 * @return {String}
 */
export const buildQueryString = (params) =>
  Object.keys(params)
    .filter((param) => params[param])
    .map((param) => `${encodeURIComponent(param)}=${encodeURIComponent(params[param])}`)
    .join('&');

/**
 * Собирает адрес по url и объекту параметров
 * @param {String} url адрес без query-параметров
 * @param {Object} params параметры
 * @return {String}
 */
export const buildUrl = (url, params) => {
  const paramsString = buildQueryString(params);
  return url + (paramsString ? '?' : '') + paramsString;
};

/**
 * Парсит querystring и возвращает объект параметров
 * @param {String} queryString строка url-параметров без адреса и ?
 * @return {Object}
 */
export const parseQueryString = (queryString) => {
  return queryString
    .split('&')
    .map((param) => {
      const [key, value] = param.split('=');
      try {
        return [decodeURIComponent(key), decodeURIComponent(value)];
      } catch (error) {
        console.warn('Invalid URI component:', param, error);
        return [key, value]; // Используем оригинальные значения, если декодировать не удалось
      }
    })
    .reduce((acc, [name, value]) => {
      return { ...acc, [name]: value };
    }, {});
};

/**
 * Выкусыват querystring из адреса url
 * @param url адрес с url-параметрами
 * @return {String}
 */
export const extractQueryString = (url) => {
  const match = url.match(/\?(.+?)($|#)/);
  return match ? match[1] : '';
};

/**
 * Парсит url и возвращает объект вида:
 *   {
 *     schema: 'https',
 *     host: 'tripster.ru',
 *     path: '/guide/123'
 *     queryString: 'param1=value&...',
 *     params: {param1: value, ...},
 *     hash: '#reviews'
 *   }
 *
 * @param {String} url
 * @return {{schema: String, host: String, path: String, params: Object, queryString: String, hash: String}}
 */
export const parseUrl = (url) => {
  const match = url.match(/(https?:)\/\/([^/]+)([/]{0,1}[^?#]*)(#.*|)/);
  const [schema, host, path, hash] = match ? [match[1], match[2], match[3], match[4]] : [null, null, null, null];
  const queryString = extractQueryString(url);
  return {
    schema,
    host,
    path,
    queryString,
    params: parseQueryString(queryString),
    hash,
  };
};

/**
 * Удаляет из строки переданные GET параметры
 * @param url {string} - Исходная строка с гет параметрами
 * @param params {[string]} - Параметр который нужно удалить
 * @returns {string}
 */
export const removeUrlParameters = (url, params) => {
  const { params: searchParams, schema, host, path, hash } = parseUrl(url);

  // вообще должен был быть omit из lodash. но его импорт ломает виджеты
  const newParamsArr = Object.entries(searchParams).filter(([key]) => !params.includes(key));
  const newParams = Object.fromEntries(newParamsArr);

  const newUrl = schema ? `${schema}//${host}${path}` : `${host}${path}`;
  return buildUrl(newUrl, newParams) + hash;
};

/**
 * Добавляет в строку GET параметры
 * @param url {string}- Исходная строка
 * @param params {object}- объект с параметрами которые будут добавлены в строку
 * @returns {string}
 */
export const addUrlParameters = (url, params) => {
  const { params: searchParams, schema, host, path, hash } = parseUrl(url);

  const newParams = {
    ...searchParams,
    ...params,
  };

  const newUrl = schema ? `${schema}//${host}${path}` : `${host}${path}`;
  return buildUrl(newUrl, newParams) + hash;
};

export const removeBackslashFromUrl = (url, position /* end|start */) => {
  if (!position) return url;

  const slashSymbol = '/';
  if (position === 'end' && url.endsWith(slashSymbol)) return url.slice(0, -1);
  if (position === 'start' && url.startsWith(slashSymbol)) return url.slice(1);
  return url;
};

/**
 * Возвращает url без домена и схемы
 * @param {String} url адрес
 * @return {String}
 */
export const withoutDomain = (url) => url.replace(/^https?:\/\/[^/]+/, '');

export const withStaticDomain = (url) => {
  const { schema, host } = parseUrl(url);
  const publicPath = removeBackslashFromUrl(process.env.PUBLIC_PATH, 'end');
  return `${schema}//${host}${publicPath}${withoutDomain(url)}`;
};

export const withWhitelabelDomain = (whitelabelUrl, targetUrl = '') => {
  const { schema, host } = parseUrl(whitelabelUrl);
  return `${schema}//${host}${withoutDomain(targetUrl)}`;
};

// TODO: этому тут не место, это про vue-router-ы, а не про url-ы
export const isSameRoute = (route1, route2) =>
  route1.matched[0]?.components.default === route2.matched[0]?.components.default;
